import { useState } from 'react';
import { Button, CircularProgress, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { DeliveryPointDetails } from '../../types/delivery-point-details.type';
import { DeliveryPointSegment, DeliveryPointType } from '../../types/delivery-point.type';
import { UpdateDeliveryPointRequestDTO } from '../../types/dtos/update-delivery-point.dto';
import { useDeliveryPointEditForm } from '../../hooks/use-delivery-point-edit-form.hook';
import { DELIVERY_POINT_SEGMENT_CHANGE_DIALOG_CONTENT } from '../../constants/delivery-point-details.constants';
import { isCompanyType } from '../../utils/delivery-point-form-rules.util';
import DeliveryPointScheduleEditor, {
  hasIncompleteScheduleBounds,
} from '../Editors/DeliveryPointScheduleEditor.component';
import DeliveryPointClosureEditor from '../Editors/DeliveryPointClosureEditor.component';
import { DeliveryPointAddressFields } from '../Forms/DeliveryPointAddressFields.component';
import { DeliveryPointCard } from './DeliveryPointCard.component';
import { DeliveryPointIdentityCard } from './DeliveryPointIdentityCard.component';
import { DeliveryPointContactCard } from './DeliveryPointContactCard.component';
import { DeliveryPointAdministrativeCard } from './DeliveryPointAdministrativeCard.component';
import { ConfirmActionDialog } from '../../../shared/components/Modals/ConfirmActionDialog.component';

const MIN_DISCOUNT = 0;
const MAX_DISCOUNT = 100;

type Props = {
  deliveryPoint: DeliveryPointDetails;
  isUpdating: boolean;
  updateDeliveryPoint: (editedFields: UpdateDeliveryPointRequestDTO) => Promise<boolean>;
};

type PendingSegmentChange = { segmentId: string | null; segment?: DeliveryPointSegment };

const useStyles = makeStyles((theme) => ({
  root: {
    '& h4': {
      margin: '0 0 0.5rem 0',
      opacity: 0.7,
    },

    '& .MuiButton-root': {
      textTransform: 'none',
    },

    '& .MuiOutlinedInput-root.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.admin.primary.main,
    },
    '& .MuiInputLabel-outlined.Mui-focused:not(.Mui-error)': {
      color: theme.palette.admin.primary.main,
    },
  },
  stackedSections: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  actionsHeader: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.75rem',
  },
  saveButton: {
    backgroundColor: theme.palette.admin.primary.main,
    color: theme.palette.admin.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.admin.primary.dark,
    },
  },
}));

export default function DeliveryPointInformationsTab({ deliveryPoint, isUpdating, updateDeliveryPoint }: Props) {
  const classes = useStyles();
  const form = useDeliveryPointEditForm(deliveryPoint);
  const { updatedDeliveryPoint } = form;

  const [pendingSegmentChange, setPendingSegmentChange] = useState<PendingSegmentChange | null>(null);
  const [isDiscountClearRefused, setIsDiscountClearRefused] = useState(false);

  const { address } = updatedDeliveryPoint;
  const isFormInvalid = form.isFormInvalid || hasIncompleteScheduleBounds(updatedDeliveryPoint.schedules);

  const handleCancel = () => {
    form.handleCancelEditing();
    setIsDiscountClearRefused(false);
  };

  const handleSubmit = async () => {
    const hasSaved = await form.handleSave(updateDeliveryPoint);
    if (!hasSaved) return;

    setIsDiscountClearRefused(false);
  };

  const handleDiscountInput = (rawValue: string) => {
    if (rawValue === '') {
      setIsDiscountClearRefused(Boolean(deliveryPoint.discount));
      if (!deliveryPoint.discount) form.handleDiscountChange(null);
      return;
    }

    const parsedDiscount = Number(rawValue);
    if (Number.isNaN(parsedDiscount) || parsedDiscount < MIN_DISCOUNT || parsedDiscount > MAX_DISCOUNT) return;

    setIsDiscountClearRefused(false);
    form.handleDiscountChange(parsedDiscount);
  };

  const handleTypeChange = (type: DeliveryPointType) => {
    form.handleTypeChange(type);
    if (!isCompanyType(type)) setIsDiscountClearRefused(false);
  };

  const handleConfirmSegmentChange = () => {
    if (!pendingSegmentChange) return;

    form.handleSegmentChange(pendingSegmentChange.segmentId, pendingSegmentChange.segment);
    setPendingSegmentChange(null);
  };

  const segmentDialogContent =
    DELIVERY_POINT_SEGMENT_CHANGE_DIALOG_CONTENT[pendingSegmentChange?.segmentId === null ? 'detach' : 'change'];

  return (
    <section className={classes.root}>
      <Grid container spacing={2}>
        <Grid item xs={12} className={classes.actionsHeader}>
          <Button size="small" variant="text" onClick={handleCancel} disabled={!form.isEditing || isUpdating}>
            Annuler
          </Button>
          <Button
            size="small"
            variant="contained"
            className={classes.saveButton}
            onClick={handleSubmit}
            disabled={!form.isEditing || isUpdating || isFormInvalid}
            startIcon={isUpdating ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {isUpdating ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </Grid>
        <Grid item xs={12} md={4} className={classes.stackedSections}>
          <DeliveryPointIdentityCard
            updatedDeliveryPoint={updatedDeliveryPoint}
            isArchived={Boolean(deliveryPoint.archivedAt)}
            codeError={form.companyCodeError}
            onFieldChange={form.handleFieldChange}
            onTypeChange={handleTypeChange}
          />
          <DeliveryPointCard title="Adresse">
            <DeliveryPointAddressFields
              idPrefix="delivery-point"
              address={address}
              addressLabel={form.addressLabel}
              isPlaceSelected={form.isPlaceSelected}
              onAddressLabelChange={form.handleAddressLabelChange}
              onAddressSelected={form.handleAddressSelected}
              onAddressFieldChange={form.handleAddressFieldChange}
            />
          </DeliveryPointCard>
        </Grid>
        <Grid item xs={12} md={3} className={classes.stackedSections}>
          <DeliveryPointContactCard
            updatedDeliveryPoint={updatedDeliveryPoint}
            onFieldChange={form.handleFieldChange}
          />
          <DeliveryPointAdministrativeCard
            updatedDeliveryPoint={updatedDeliveryPoint}
            attachedSegment={deliveryPoint.segment}
            isDiscountClearRefused={isDiscountClearRefused}
            onSegmentSelect={(segment) =>
              setPendingSegmentChange(segment ? { segmentId: segment.id, segment } : { segmentId: null })
            }
            onFieldChange={form.handleFieldChange}
            onDiscountInput={handleDiscountInput}
          />
        </Grid>
        <Grid item xs={12} md={5} className={classes.stackedSections}>
          <DeliveryPointCard title="Horaires">
            <DeliveryPointScheduleEditor
              schedules={updatedDeliveryPoint.schedules}
              onChange={form.handleSchedulesChange}
            />
          </DeliveryPointCard>
          <DeliveryPointCard title="Fermetures">
            <DeliveryPointClosureEditor closures={updatedDeliveryPoint.closures} onChange={form.handleClosuresChange} />
          </DeliveryPointCard>
        </Grid>
      </Grid>
      {pendingSegmentChange && (
        <ConfirmActionDialog
          open
          title={segmentDialogContent.title}
          description={segmentDialogContent.description}
          confirmLabel={segmentDialogContent.confirmLabel}
          onConfirm={handleConfirmSegmentChange}
          onClose={() => setPendingSegmentChange(null)}
        />
      )}
    </section>
  );
}
